class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    const trapDrawerFocus = () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    };

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      trapDrawerFocus();
    } else {
      this.addEventListener('transitionend', trapDrawerFocus, { once: true });
    }

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.classList.remove('is-empty');
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
        childSectionSelectors: [
          '.drawer__heading',
          '.cart__free-shipping',
          '.drawer__contents',
          '.cart-drawer__footer-info'
        ]
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  constructor() {
    super();
    this.cartDrawerUpsells = this.querySelector('.cart-drawer-recommendations');
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.js-contents',
        childSectionSelectors: [
          '.drawer__heading',
          '.cart__free-shipping',
          '.drawer__contents',
          '.cart-drawer__footer-info'
        ]
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }

  closeUpsells(cartDrawerInner) {
    // The upsell block is laid out inline (no slide transition), so don't wait for a
    // transitionend that never fires — hide it right away.
    if (cartDrawerInner) cartDrawerInner.classList.remove('drawer__inner--no-border');
    this.cartDrawerUpsells.classList.add('close');
  }

  updateUpsells(cartRecommendationsHtml, cartDrawerInner) {
    if (cartRecommendationsHtml === "") {
      this.cartDrawerUpsells.classList.remove('cart-drawer-recommendations--loading');
      this.closeUpsells(cartDrawerInner);
    } else {
      const cartDrawerRecommendationsList = this.cartDrawerUpsells.querySelector('.cart-drawer-recommendations__list');
      if (cartDrawerRecommendationsList) {
        cartDrawerRecommendationsList.classList.add('disable-list');
        if (cartDrawerInner) cartDrawerInner.classList.add('drawer__inner--no-border');
        this.cartDrawerUpsells.classList.remove('close');
      }

      setTimeout(() => {
        this.cartDrawerUpsells.innerHTML = cartRecommendationsHtml;
      }, 200);
    }
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class CartRecommendations extends HTMLElement {
  constructor() {
    super();
  }

  collapseEmpty(reason) {
    if (this.classList.contains('product-recommendations--loaded')) return;
    this.classList.add('product-recommendations--loaded');
    const cartDrawerUpsells = this.closest('.cart-drawer-recommendations');
    if (!cartDrawerUpsells) return;
    cartDrawerUpsells.classList.remove('cart-drawer-recommendations--loading');
    cartDrawerUpsells.classList.add('close', 'cart-drawer-recommendations--empty');
    const cartDrawerInner = cartDrawerUpsells.closest('.drawer__inner');
    if (cartDrawerInner) cartDrawerInner.classList.remove('drawer__inner--no-border');
    if (reason) cartDrawerUpsells.dataset.collapseReason = reason;
  }

  snapshotHydratedUpsells(cartDrawerUpsells) {
    const drawer = cartDrawerUpsells ? cartDrawerUpsells.closest('cart-drawer') : null;
    if (!drawer || !cartDrawerUpsells.querySelector('product-item')) return;
    const snapshot = cartDrawerUpsells.cloneNode(true);
    snapshot.classList.remove('close', 'cart-drawer-recommendations--empty', 'cart-drawer-recommendations--loading');
    snapshot.removeAttribute('hidden');
    drawer._setupUpsellsSnapshot = snapshot;
  }

  connectedCallback() {
    const cartDrawerUpsells = this.closest('.cart-drawer-recommendations');
    if (this.classList.contains('product-recommendations--loaded') || this.querySelector('product-item')) {
      this.classList.add('product-recommendations--loaded');
      if (cartDrawerUpsells) {
        cartDrawerUpsells.classList.remove('cart-drawer-recommendations--loading');
        this.snapshotHydratedUpsells(cartDrawerUpsells);
      }
      return;
    }

    if (!cartDrawerUpsells || !this.dataset.url) {
      this.collapseEmpty('missing');
      return;
    }

    const giveUp = setTimeout(() => this.collapseEmpty('timeout'), 2000);

    fetch(this.dataset.url)
      .then(response => response.text())
      .then(text => {
        clearTimeout(giveUp);
        if (this.classList.contains('product-recommendations--loaded')) {
          return;
        }
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('cart-recommendations');

        if (recommendations && recommendations.innerHTML.trim().length) {
          cartDrawerUpsells.classList.remove('cart-drawer-recommendations--loading');
          this.innerHTML = recommendations.innerHTML;
          this.querySelectorAll('product-item').forEach((item) => {
            const href = (item.querySelector('a[href]') && item.querySelector('a[href]').getAttribute('href')) || '';
            if (href.indexOf('/products/cork-yoga-block') !== -1) item.remove();
          });
          const cartDrawerRecommendationsList = this.querySelector('.cart-drawer-recommendations__list');

          if (cartDrawerRecommendationsList) {
            setTimeout(() => {
              const list = this.querySelector('.cart-drawer-recommendations__list');
              if (list) list.classList.remove('disable-list');
            });
          }
        }

        const cartDrawerInner = cartDrawerUpsells.closest('.drawer__inner');
        const hasUpsellItems = Boolean(this.querySelector('product-item'));
        const freeShipping = this.closest('cart-drawer')?.querySelector('cart-free-shipping')
          || document.querySelector('cart-drawer cart-free-shipping');
        const shippingReached = Boolean(
          freeShipping &&
          typeof freeShipping.hasReachedThreshold === 'function' &&
          freeShipping.hasReachedThreshold()
        );

        if (!hasUpsellItems) {
          cartDrawerUpsells.classList.add('close', 'cart-drawer-recommendations--empty');
          setTimeout(() => {
            if (cartDrawerInner) cartDrawerInner.classList.remove('drawer__inner--no-border');
          }, 200);
        } else if (shippingReached && cartDrawerUpsells.dataset.upsellsSource !== 'reverse-mat') {
          cartDrawerUpsells.classList.add('close');
          cartDrawerUpsells.setAttribute('hidden', '');
          cartDrawerUpsells.classList.remove('cart-drawer-recommendations--empty', 'cart-drawer-recommendations--loading');
          cartDrawerUpsells.querySelectorAll('button[type="submit"]').forEach((button) => {
            button.disabled = false;
            button.removeAttribute('aria-disabled');
            button.classList.remove('btn--loading');
          });
          this.snapshotHydratedUpsells(cartDrawerUpsells);
          if (cartDrawerInner) cartDrawerInner.classList.remove('drawer__inner--no-border');
        } else {
          if (cartDrawerInner) cartDrawerInner.classList.add('drawer__inner--no-border');
          cartDrawerUpsells.classList.remove('close', 'cart-drawer-recommendations--empty', 'cart-drawer-recommendations--loading');
          cartDrawerUpsells.removeAttribute('hidden');
          cartDrawerUpsells.querySelectorAll('button[type="submit"]').forEach((button) => {
            button.disabled = false;
            button.removeAttribute('aria-disabled');
            button.classList.remove('btn--loading');
          });
          this.snapshotHydratedUpsells(cartDrawerUpsells);
        }

        this.classList.add('product-recommendations--loaded');

        if (this.classList.contains('complementary-products') ) {
          this.querySelectorAll('script').forEach(oldScriptTag => {
            const newScriptTag = document.createElement('script');
            Array.from(oldScriptTag.attributes).forEach(attribute => {
              newScriptTag.setAttribute(attribute.name, attribute.value)
            });
            newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
            oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
          });
        }
      })
      .catch(e => {
        clearTimeout(giveUp);
        console.error(e);
        this.collapseEmpty('error');
      });
  }
}

customElements.define('cart-recommendations', CartRecommendations);
